import { useState, useRef, useEffect } from 'react';
import { uploadMediaFile } from '@/hooks/useUpload';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import {
  updateVendorShopProfile,
  fetchVendorBookingServices,
  fetchVendorDefaultBookingService,
  setVendorDefaultBookingService,
  fetchVendorRelation,
  updateVendorCategory,
  fetchMarketplaceCategories,
  type VendorBookingService,
  type MarketplaceCategory,
} from '@/lib/marketplaceCatalog';
import { useAuth, type ZifekUser } from '@/hooks/useAuth';
import { WEEKDAYS, normalizeOpeningHours, type OpeningHours } from '@/lib/restaurantHours';
import VendorBannersTab from './VendorBannersTab';
import type { AnnonceItem } from './VendorProductForm';

// ─────────────────────────────────────────────────────────────
// Paramètres de la boutique vendeur.
// Permet de changer le logo affiché sur la boutique publique,
// de modifier les informations de contact et de gérer les
// bannières mises en avant sur la page boutique.
// ─────────────────────────────────────────────────────────────

interface VendorSettingsProps {
  user: ZifekUser;
  marketplaceId?: number;
  annonces: AnnonceItem[];
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === 'object') {
    const obj = err as { message?: string; details?: string };
    return obj.message || obj.details || '';
  }
  return '';
}

export default function VendorSettings({ user, marketplaceId, annonces }: VendorSettingsProps) {
  const { updateUser } = useAuth();

  // ── Logo ──
  const logoRef = useRef<HTMLInputElement>(null);
  const [logo, setLogo] = useState(user.image || '');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // ── Informations boutique ──
  const [nomcommerce, setNomcommerce] = useState(user.nomcommerce || user.name || '');
  const [description, setDescription] = useState(user.description || '');
  const [telephone, setTelephone] = useState(user.telephone || '');
  const [savingInfo, setSavingInfo] = useState(false);

  // ── Horaires & réseaux sociaux ──
  const [hours, setHours] = useState<OpeningHours>(() => normalizeOpeningHours(user.horaires_json));
  const [facebook, setFacebook] = useState(user.lienfacebook || '');
  const [instagram, setInstagram] = useState(user.lieninstagram || '');
  const [tiktok, setTiktok] = useState(user.lientiktok || '');
  const [twitter, setTwitter] = useState(user.lientwitter || '');
  const [linkedin, setLinkedin] = useState(user.lienlinkedin || '');
  const [youtube, setYoutube] = useState(user.lienyoutube || '');
  const [savingSocial, setSavingSocial] = useState(false);

  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const flashSaved = () => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2200);
  };

  // ── Service de réservation par défaut ──
  const [bookingServices, setBookingServices] = useState<VendorBookingService[]>([]);
  const [defaultBookingId, setDefaultBookingId] = useState<number | null>(null);
  const [savingBooking, setSavingBooking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [services, def] = await Promise.all([
          fetchVendorBookingServices(user.id),
          fetchVendorDefaultBookingService(user.id),
        ]);
        if (cancelled) return;
        setBookingServices(services);
        // Si le service par défaut n'existe plus, on retombe sur le premier.
        const valid = def && services.some((s) => s.id === def) ? def : (services[0]?.id ?? null);
        setDefaultBookingId(valid);
      } catch { /* silencieux */ }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  // ── Catégorie, sous-catégorie & sous-sous-catégorie de la boutique ──
  const [relationId, setRelationId] = useState<number | null>(null);
  const [commerceCategories, setCommerceCategories] = useState<MarketplaceCategory[]>([]);
  const [catId, setCatId] = useState<number | null>(null);
  const [subCatId, setSubCatId] = useState<number | null>(null);
  const [subSubCatId, setSubSubCatId] = useState<number | null>(null);

  useEffect(() => {
    if (!marketplaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const [cats, rel] = await Promise.all([
          fetchMarketplaceCategories(marketplaceId),
          fetchVendorRelation(marketplaceId, user.id),
        ]);
        if (cancelled) return;
        setCommerceCategories(cats);
        if (rel) {
          setRelationId(rel.relationId);
          setCatId(rel.categorieId);
          setSubCatId(rel.souscategorieId);
          setSubSubCatId(rel.soussouscategorieId);
        }
      } catch { /* silencieux */ }
    })();
    return () => { cancelled = true; };
  }, [marketplaceId, user.id]);

  const rootCommerceCats = commerceCategories.filter((c) => c.parent_id == null || Number(c.parent_id) === 0);
  const subCommerceCats = catId ? commerceCategories.filter((c) => Number(c.parent_id) === catId) : [];
  const subSubCommerceCats = subCatId ? commerceCategories.filter((c) => Number(c.parent_id) === subCatId) : [];

  const saveBooking = async (id: number | null) => {
    setSavingBooking(true);
    setError('');
    try {
      await setVendorDefaultBookingService(user.id, id);
      setDefaultBookingId(id);
      flashSaved();
    } catch (err) {
      setError(errorMessage(err) || "Erreur lors de l'enregistrement.");
    } finally {
      setSavingBooking(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    setError('');
    try {
      const url = await uploadMediaFile(file, 'logos');
      await updateVendorShopProfile(user.id, { image: url });
      setLogo(url);
      updateUser({ image: url });
      flashSaved();
    } catch (err) {
      setError(errorMessage(err) || "Échec de l'upload du logo");
    } finally {
      setUploadingLogo(false);
      if (logoRef.current) logoRef.current.value = '';
    }
  };

  const removeLogo = async () => {
    setError('');
    try {
      await updateVendorShopProfile(user.id, { image: '' });
      setLogo('');
      updateUser({ image: '' });
      flashSaved();
    } catch (err) {
      setError(errorMessage(err) || 'Impossible de retirer le logo.');
    }
  };

  const saveInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nomcommerce.trim()) {
      setError('Le nom de la boutique est obligatoire.');
      return;
    }
    setSavingInfo(true);
    setError('');
    try {
      await updateVendorShopProfile(user.id, {
        nomcommerce: nomcommerce.trim(),
        description: description.trim(),
        telephone: telephone.trim(),
      });
      // Catégorie & sous-catégorie déclarées (rattachement à la marketplace).
      if (relationId) {
        const catNom = commerceCategories.find((c) => c.id === catId)?.nom || null;
        const subNom = commerceCategories.find((c) => c.id === subCatId)?.nom || null;
        const subSubNom = commerceCategories.find((c) => c.id === subSubCatId)?.nom || null;
        await updateVendorCategory(relationId, catId, catNom, subCatId, subNom, subSubCatId, subSubNom);
      }
      updateUser({
        nomcommerce: nomcommerce.trim(),
        description: description.trim(),
        telephone: telephone.trim(),
      });
      flashSaved();
    } catch (err) {
      setError(errorMessage(err) || "Erreur lors de l'enregistrement.");
    } finally {
      setSavingInfo(false);
    }
  };

  const saveSocials = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSocial(true);
    setError('');
    try {
      const patch = {
        horaires_json: hours,
        horaires: '',
        lienfacebook: facebook.trim(),
        lieninstagram: instagram.trim(),
        lientiktok: tiktok.trim(),
        lientwitter: twitter.trim(),
        lienlinkedin: linkedin.trim(),
        lienyoutube: youtube.trim(),
      };
      await updateVendorShopProfile(user.id, patch);
      updateUser(patch);
      flashSaved();
    } catch (err) {
      setError(errorMessage(err) || "Erreur lors de l'enregistrement.");
    } finally {
      setSavingSocial(false);
    }
  };

  const logoPreview = resolveMediaUrl(logo);

  const setDay = (key: string, patch: Partial<OpeningHours[string]>) => {
    setHours((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };
  const toggleDay = (key: string) => {
    setHours((prev) => ({ ...prev, [key]: { ...prev[key], closed: !prev[key].closed } }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold font-heading text-foreground-950 flex items-center gap-2 mb-1">
          <i className="ri-settings-3-line text-primary-500"></i>
          Paramètres de la boutique
        </h2>
        <p className="text-sm text-foreground-500">
          Personnalisez votre logo, vos informations et vos bannières visibles sur la marketplace.
        </p>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {saved && (
        <div className="px-4 py-3 rounded-lg bg-accent-100/70 border border-accent-200 text-xs text-accent-900 flex items-center gap-2">
          <i className="ri-check-line"></i>
          Modifications enregistrées.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Logo */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
            <i className="ri-image-2-line text-primary-500"></i>
            Logo de la boutique
          </h3>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-background-100 border border-background-200/70 overflow-hidden flex items-center justify-center flex-shrink-0">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo boutique" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-foreground-400 font-heading">
                  {(nomcommerce || 'V').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1">
              <input ref={logoRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => logoRef.current?.click()}
                  disabled={uploadingLogo}
                  className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-background-200/70 bg-background-50 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className={uploadingLogo ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'}></i>
                  {uploadingLogo ? 'Upload...' : logoPreview ? 'Changer le logo' : 'Choisir un logo'}
                </button>
                {logoPreview && (
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-background-100 text-xs font-medium text-foreground-500 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                    Retirer
                  </button>
                )}
              </div>
              <p className="mt-2 text-[11px] text-foreground-400">
                Format carré recommandé (max 6 Mo). Ce logo apparaît en tête de votre boutique.
              </p>
            </div>
          </div>
        </div>

        {/* Informations */}
        <form onSubmit={saveInfo} className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
            <i className="ri-store-2-line text-primary-500"></i>
            Informations de la boutique
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nom de la boutique</label>
              <input
                type="text"
                value={nomcommerce}
                onChange={(e) => setNomcommerce(e.target.value)}
                maxLength={90}
                className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Téléphone</label>
              <input
                type="text"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                maxLength={30}
                placeholder="Ex : +212 6 12 34 56 78"
                className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={300}
                placeholder="Présentez votre activité en quelques mots..."
                className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
              />
            </div>
            {marketplaceId ? (
              <div className="pt-3 border-t border-background-200/60 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Catégorie de la boutique</label>
                  {rootCommerceCats.length === 0 ? (
                    <p className="text-xs text-foreground-400">La marketplace n&apos;a pas encore créé de catégories.</p>
                  ) : (
                    <select
                      value={catId ?? ''}
                      onChange={(e) => {
                        setCatId(e.target.value ? Number(e.target.value) : null);
                        setSubCatId(null);
                        setSubSubCatId(null);
                      }}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                    >
                      <option value="">Aucune</option>
                      {rootCommerceCats.map((c) => (
                        <option key={c.id} value={c.id}>{c.nom}</option>
                      ))}
                    </select>
                  )}
                </div>
                {subCommerceCats.length > 0 && (
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Sous-catégorie</label>
                    <select
                      value={subCatId ?? ''}
                      onChange={(e) => {
                        setSubCatId(e.target.value ? Number(e.target.value) : null);
                        setSubSubCatId(null);
                      }}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                    >
                      <option value="">Aucune</option>
                      {subCommerceCats.map((c) => (
                        <option key={c.id} value={c.id}>{c.nom}</option>
                      ))}
                    </select>
                  </div>
                )}
                {subSubCommerceCats.length > 0 && (
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Sous-sous-catégorie</label>
                    <select
                      value={subSubCatId ?? ''}
                      onChange={(e) => setSubSubCatId(e.target.value ? Number(e.target.value) : null)}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                    >
                      <option value="">Aucune</option>
                      {subSubCommerceCats.map((c) => (
                        <option key={c.id} value={c.id}>{c.nom}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ) : null}
            <button
              type="submit"
              disabled={savingInfo}
              className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2"
            >
              {savingInfo ? (
                <><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>
              ) : (
                <><i className="ri-save-line"></i>Enregistrer les informations</>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Horaires & réseaux sociaux */}
      <form onSubmit={saveSocials} className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-time-line text-primary-500"></i>
          Horaires &amp; réseaux sociaux
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Horaires d&apos;ouverture</label>
            <div className="rounded-lg border border-background-200/70 divide-y divide-background-200/60 overflow-hidden">
              {WEEKDAYS.map((d) => {
                const day = hours[d.key];
                return (
                  <div key={d.key} className="flex items-center gap-2 px-3 py-2">
                    <span className="w-20 text-xs font-medium text-foreground-700 flex-shrink-0">{d.label}</span>
                    <button
                      type="button"
                      onClick={() => toggleDay(d.key)}
                      className={`w-16 px-2 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                        day.closed ? 'bg-background-100 text-foreground-500' : 'bg-accent-100 text-accent-800'
                      }`}
                    >
                      {day.closed ? 'Fermé' : 'Ouvert'}
                    </button>
                    {day.closed ? (
                      <span className="text-xs text-foreground-400">Aucun créneau</span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="time"
                          value={day.open}
                          onChange={(e) => setDay(d.key, { open: e.target.value })}
                          className="px-2 py-1 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                        />
                        <span className="text-foreground-400 text-xs">—</span>
                        <input
                          type="time"
                          value={day.close}
                          onChange={(e) => setDay(d.key, { close: e.target.value })}
                          className="px-2 py-1 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-foreground-400">
              Un badge « Ouvert / Fermé maintenant » s&apos;affichera automatiquement sur votre boutique.
            </p>
          </div>
          <div className="space-y-3">
            {[
              { icon: 'ri-facebook-circle-line', label: 'Facebook', value: facebook, set: setFacebook, ph: 'https://facebook.com/mapage' },
              { icon: 'ri-instagram-line', label: 'Instagram', value: instagram, set: setInstagram, ph: 'https://instagram.com/moncompte' },
              { icon: 'ri-tiktok-line', label: 'TikTok', value: tiktok, set: setTiktok, ph: 'https://tiktok.com/@moncompte' },
              { icon: 'ri-twitter-x-line', label: 'X / Twitter', value: twitter, set: setTwitter, ph: 'https://x.com/moncompte' },
              { icon: 'ri-linkedin-box-line', label: 'LinkedIn', value: linkedin, set: setLinkedin, ph: 'https://linkedin.com/company/monentreprise' },
              { icon: 'ri-youtube-line', label: 'YouTube', value: youtube, set: setYoutube, ph: 'https://youtube.com/@machaine' },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${s.icon} text-foreground-500`}></i>
                </span>
                <input
                  type="url"
                  value={s.value}
                  onChange={(e) => s.set(e.target.value)}
                  maxLength={200}
                  placeholder={s.ph}
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>
            ))}
          </div>
        </div>
        <button
          type="submit"
          disabled={savingSocial}
          className="mt-4 w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2"
        >
          {savingSocial ? (
            <><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>
          ) : (
            <><i className="ri-save-line"></i>Enregistrer horaires &amp; réseaux</>
          )}
        </button>
      </form>

      {/* Service de réservation par défaut */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-1 flex items-center gap-2">
          <i className="ri-calendar-check-line text-primary-500"></i>
          Service de réservation par défaut
        </h3>
        <p className="text-xs text-foreground-500 mb-3">
          Le service proposé en priorité sur votre boutique et via le bouton « Réserver » de vos cartes de service.
        </p>
        {bookingServices.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-200/70 py-6 text-center">
            <p className="text-xs text-foreground-400">
              Aucun service de réservation. Créez-en un depuis Réservations pour activer la prise de rendez-vous.
            </p>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <select
              value={defaultBookingId ?? ''}
              onChange={(e) => saveBooking(e.target.value ? Number(e.target.value) : null)}
              disabled={savingBooking}
              className="w-full sm:w-auto min-w-[240px] px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer disabled:opacity-60"
            >
              <option value="">Aucun (prendre le premier service)</option>
              {bookingServices.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.service_name}{s.duration ? ` · ${s.duration} min` : ''}
                </option>
              ))}
            </select>
            {savingBooking && <i className="ri-loader-4-line animate-spin text-primary-500"></i>}
          </div>
        )}
      </div>

      {/* Bannières */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-image-2-line text-primary-500"></i>
          Bannières de la boutique
        </h3>
        <VendorBannersTab marketplaceId={marketplaceId} vendorId={user.id} annonces={annonces} />
      </div>
    </div>
  );
}